import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';
import DoctorPatientInfo from '../components/DoctorPatientInfo';
import CallTypeBadge from '../components/CallTypeBadge';
import SendPrescriptionModal from '../components/SendPrescriptionModal';
import PatientRecordsModal from '../components/PatientRecordsModal';
import DoctorNavbar from '../components/DoctorNavbar';
import FilterSelect from '../components/FilterSelect';
import { toast } from 'react-toastify';
import { clearDoctorSessionLocal, setDoctorSession } from '../utils/auth';
import { useDoctorLiveRefresh } from '../hooks/useDoctorLiveRefresh';

const SLOT_DURATION_OPTIONS = [
  { value: '15', label: '15 Minutes' },
  { value: '30', label: '30 Minutes' },
  { value: '45', label: '45 Minutes' },
  { value: '60', label: '60 Minutes' },
];

// Inject pulse animation style
const pulseStyle = document.createElement('style');
pulseStyle.textContent = `
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.8; transform: scale(1.02); }
  }
`;
if (!document.querySelector('style[data-pulse]')) {
  pulseStyle.setAttribute('data-pulse', 'true');
  document.head.appendChild(pulseStyle);
}

function DoctorAppointments() {
  /* ---------------- STATE ---------------- */
  const navigate = useNavigate();
  const location = useLocation(); // Add useLocation hook
  const [doctorId, setDoctorId] = useState(null); // Added for polling
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('slots'); // pending, upcoming, rejected, slots, booked
  const [videoSessionStatus, setVideoSessionStatus] = useState({}); // Track which appointments have joined calls
  const [searchQuery, setSearchQuery] = useState(''); // Search filter
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [appointmentToReject, setAppointmentToReject] = useState(null);
  const [slotToDelete, setSlotToDelete] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [prescriptionAppointment, setPrescriptionAppointment] = useState(null);
  const [recordsAppointment, setRecordsAppointment] = useState(null);
  const [slotForm, setSlotForm] = useState({
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    duration: '30',
    days: [],
    call_type: 'VIDEO',
  });

  const mediaUrl = (file) => {
    if (!file) return null;
    if (file.startsWith('http')) return file;
    const base = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    return file.startsWith('/') ? `${base}${file}` : `${base}/${file}`;
  };

  /* ---------------- EFFECTS ---------------- */

  useEffect(() => {
    // Check if there's a requested tab in the location state
    if (location.state && location.state.activeTab) {
      setActiveTab(location.state.activeTab);
    }
    checkSession();
  }, [location.state]);

  useDoctorLiveRefresh(doctorId, (id, isBackground) => {
    loadDashboardData(id, !isBackground, isBackground);
  });

  const checkSession = async () => {
    try {
      const res = await api.get('doctor/session-check/');
      if (res.data.authenticated) {
        setDoctorSession(res.data.doctor_id);
        setDoctorId(res.data.doctor_id);
        loadDashboardData(res.data.doctor_id);
      } else {
        clearDoctorSessionLocal();
        navigate('/doctor/login', { replace: true });
      }
    } catch (err) {
      console.error('Doctor Session check failed:', err);
      clearDoctorSessionLocal();
      navigate('/doctor/login', { replace: true });
    }
  };



  const loadDashboardData = async (doctorId, showLoading = true, appointmentsOnly = false) => {
    try {
      if (showLoading) setLoading(true);

      if (appointmentsOnly) {
        const appointmentsRes = await api.get('appointments/doctor/my-appointments/');
        setAppointments(appointmentsRes.data);
        return;
      }

      const [profileRes, appointmentsRes, slotsRes] = await Promise.all([
        api.get(`doctor/profile/${doctorId}/`),
        api.get('appointments/doctor/my-appointments/'),
        api.get('appointments/doctor/slots/all/')
      ]);

      setDoctorInfo(profileRes.data);
      setAppointments(appointmentsRes.data);
      setSlots(slotsRes.data);

      // Fetch video session status for all accepted appointments
      const acceptedAppointments = appointmentsRes.data.filter(a =>
        a.status === 'ACCEPTED' || a.status === 'CONFIRMED'
      );

      const sessionStatusMap = {};
      await Promise.all(
        acceptedAppointments.map(async (appointment) => {
          try {
            const sessionRes = await api.get(`video/session-info/${appointment.appointment_id}/`);
            sessionStatusMap[appointment.appointment_id] = {
              userJoined: sessionRes.data?.user_joined_at !== null && sessionRes.data?.user_joined_at !== undefined,
              doctorJoined: sessionRes.data?.doctor_joined_at !== null && sessionRes.data?.doctor_joined_at !== undefined
            };
          } catch (err) {
            // If session doesn't exist, user didn't join
            sessionStatusMap[appointment.appointment_id] = {
              userJoined: false,
              doctorJoined: false
            };
          }
        })
      );
      setVideoSessionStatus(sessionStatusMap);
    } catch (err) {
      console.error("Error loading doctor appointments data:", err);
      // Only redirect on auth error if it's the initial load
      if (err.response?.status === 401 && showLoading) {
        clearDoctorSessionLocal();
        navigate('/doctor/login', { replace: true });
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const updateBookingStatus = async (appointmentId, action, reason = "") => {
    try {
      await api.post('appointments/doctor/booking/update/', {
        appointment_id: appointmentId,
        action: action,
        reason: reason
      });
      // Refresh appointments
      const appointmentsRes = await api.get('appointments/doctor/my-appointments/');
      setAppointments(appointmentsRes.data);
      toast.success(`Booking ${action}ed successfully!`, {
        position: "bottom-right",
        autoClose: 3000,
      });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update booking status", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  const toggleSlotStatus = async (slotId) => {
    try {
      await api.post(`appointments/doctor/slots/toggle-status/${slotId}/`);
      const slotsRes = await api.get('appointments/doctor/slots/all/');
      setSlots(slotsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update slot status", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  const handleDeleteSlotClick = (slotId) => {
    setSlotToDelete(slotId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteSlotConfirm = async () => {
    if (!slotToDelete) return;

    try {
      await api.delete(`appointments/doctor/slots/delete/${slotToDelete}/`);
      const slotsRes = await api.get('appointments/doctor/slots/all/');
      setSlots(slotsRes.data);
      toast.success("Slot deleted successfully!", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete slot. Note: Booked slots cannot be deleted.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } finally {
      setShowDeleteConfirm(false);
      setSlotToDelete(null);
    }
  };

  const handleCreateSlot = async (e) => {
    e.preventDefault();

    if (slotForm.days.length === 0) {
      toast.warning("Please select at least one day", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      const response = await api.post('appointments/doctor/slots/create/', slotForm);
      setShowSlotModal(false);
      setSlotForm({ start_date: '', end_date: '', start_time: '', end_time: '', duration: '30', days: [], call_type: 'VIDEO' });
      const slotsRes = await api.get('appointments/doctor/slots/all/');
      setSlots(slotsRes.data);
      toast.success(response.data?.msg || "Slots created successfully!", {
        position: "bottom-right",
        autoClose: 3000,
      });
      if (response.data?.warning) {
        toast.warning(response.data.warning, {
          position: "bottom-right",
          autoClose: 5000,
        });
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || err.response?.data?.msg || "Failed to create slots";
      toast.error(errMsg, {
        position: "bottom-right",
        autoClose: err.response?.status === 409 ? 6000 : 3000,
      });
    }
  };


  const toggleDay = (day) => {
    setSlotForm(prev => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter(d => d !== day)
        : [...prev.days, day]
    }));
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const pendingAppointments = appointments.filter(a => a.status === 'PENDING');
  const upcomingAppointments = appointments.filter(a => {
    if (a.status !== 'ACCEPTED' || a.booking_type !== 'UPCOMING') return false;
    // Check if the appointment is actually in the future
    const appointmentDateTime = new Date(`${a.date} ${a.start_time}`);
    const now = new Date();
    return appointmentDateTime >= now; // Only include future appointments
  });

  // Session Expired: Booked but call was never joined (end time passed, user_joined_at is null)
  const sessionExpiredAppointments = appointments.filter(a => {
    if (a.status === 'COMPLETED' || a.status === 'MISSED') return false;
    if (a.status !== 'ACCEPTED' && a.status !== 'CONFIRMED') return false;
    // Check if the appointment end time has passed
    if (a.date && a.end_time) {
      const appointmentEndDateTime = new Date(`${a.date} ${a.end_time}`);
      const now = new Date();
      const gracePeriod = 5 * 60 * 1000; // 5 minutes
      const isPast = appointmentEndDateTime.getTime() + gracePeriod < now.getTime();

      if (isPast) {
        // Check if user joined the call
        const sessionInfo = videoSessionStatus[a.appointment_id];
        return !sessionInfo?.userJoined; // User didn't join
      }
    }
    return false;
  });

  // Completed Session: Backend marks as COMPLETED when call ends, or legacy: past ACCEPTED with user joined
  const completedSessionAppointments = appointments.filter(a => {
    if (a.status === 'COMPLETED') return true;
    if (a.status !== 'ACCEPTED' && a.status !== 'CONFIRMED') return false;
    if (a.date && a.end_time) {
      const appointmentEndDateTime = new Date(`${a.date} ${a.end_time}`);
      const now = new Date();
      const gracePeriod = 5 * 60 * 1000; // 5 minutes
      const isPast = appointmentEndDateTime.getTime() + gracePeriod < now.getTime();
      if (isPast) {
        const sessionInfo = videoSessionStatus[a.appointment_id];
        return sessionInfo?.userJoined === true;
      }
    }
    return false;
  });

  const rejectedAppointments = appointments.filter(a => a.status === 'REJECTED');
  const missedAppointments = appointments.filter(a => a.status === 'MISSED');

  // Normalize for flexible search (times, dates, call type labels)
  const normalizeSearchText = (value) =>
    String(value || '')
      .toLowerCase()
      .trim()
      .replace(/[:\s\-/,.]/g, '');

  const matchesQuery = (query, ...fields) => {
    const q = query.toLowerCase().trim();
    if (!q) return true;
    const qCompact = normalizeSearchText(q);

    return fields.some((field) => {
      const text = String(field || '').toLowerCase();
      if (!text) return false;
      if (text.includes(q)) return true;
      const compact = normalizeSearchText(text);
      return compact.includes(qCompact);
    });
  };

  const findAppointmentForSlot = (slot) => {
    const slotDate = slot.date || slot.date_from;
    if (!slotDate || !slot.start_time) return null;
    const slotDateStr = new Date(slotDate).toDateString();
    const slotStart = String(slot.start_time).slice(0, 5);
    const slotEnd = String(slot.end_time || '').slice(0, 5);

    return appointments.find((a) => {
      if (!a.date || !a.start_time) return false;
      const apptDateStr = new Date(a.date).toDateString();
      if (apptDateStr !== slotDateStr) return false;
      const aStart = String(a.start_time).slice(0, 5);
      const aEnd = String(a.end_time || '').slice(0, 5);
      return aStart === slotStart && (!slotEnd || !aEnd || aEnd === slotEnd);
    }) || null;
  };

  // Search filter helper for appointments
  const filterBySearch = (appointmentList) => {
    if (!searchQuery.trim()) return appointmentList;

    const query = searchQuery.trim();
    return appointmentList.filter((a) =>
      matchesQuery(
        query,
        a.patient_name,
        a.patient_email,
        a.booked_by_display,
        a.booked_by_name,
        a.patient_relation,
        a.date,
        a.start_time,
        a.end_time,
        a.call_type,
        a.call_type === 'AUDIO' ? 'audio call' : 'video call',
        a.status
      )
    );
  };

  // Filter slots by search (includes linked patient for booked slots)
  const filterSlotsBySearch = (slotsList) => {
    if (!searchQuery.trim()) return slotsList;

    const query = searchQuery.trim();
    return slotsList.filter((s) => {
      const matchingAppointment = findAppointmentForSlot(s);
      return matchesQuery(
        query,
        s.date || s.date_from,
        s.start_time,
        s.end_time,
        s.day_of_week,
        s.duration,
        s.call_type,
        s.call_type === 'AUDIO' ? 'audio call' : 'video call',
        matchingAppointment?.patient_name,
        matchingAppointment?.patient_email,
        matchingAppointment?.booked_by_display,
        matchingAppointment?.booked_by_name,
        matchingAppointment?.patient_relation,
        matchingAppointment?.status
      );
    });
  };

  return (
    <div className="bg-light min-vh-100">
      <DoctorNavbar />

      <div className="container py-4">

        {/* HEADER SECTION */}
        <div className="row mb-4 align-items-center">
          <div className="col">
            <button
              className="btn btn-link text-decoration-none text-muted p-0 mb-2"
              onClick={() => navigate('/doctor/dashboard')}
            >
              <i className="bi bi-arrow-left fs-5 me-2"></i>Back to Dashboard
            </button>
            <h2 className="fw-bold text-dark mb-0">All Appointments & Slots</h2>
            <p className="text-muted">Manage all your appointments and availability slots.</p>
          </div>
        </div>

        {/* FILTER AND SEARCH */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body p-3">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-4">
                <label className="form-label small fw-bold text-muted mb-2">Filter by Status</label>
                <FilterSelect
                  value={activeTab}
                  onChange={setActiveTab}
                  options={[
                    { value: 'pending', label: `Pending (${pendingAppointments.length})` },
                    { value: 'upcoming', label: `Upcoming (${upcomingAppointments.length})` },
                    { value: 'session-expired', label: `Session Expired (${sessionExpiredAppointments.length})` },
                    { value: 'completed-session', label: `Completed Session (${completedSessionAppointments.length})` },
                    { value: 'missed', label: `Missed (${missedAppointments.length})` },
                    { value: 'rejected', label: `Rejected (${rejectedAppointments.length})` },
                    { value: 'slots', label: `All Slots (${slots.length})` },
                    { value: 'booked', label: `Booked Slots (${slots.filter(s => s.is_booked).length})` },
                  ]}
                  placeholder="Filter by Status"
                  aria-label="Filter by Status"
                  variant="bordered"
                />
              </div>
              <div className="col-12 col-md-8">
                <label className="form-label small fw-bold text-muted mb-2">Search</label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0">
                    <i className="bi bi-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Search patient, email, date, time, audio/video..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="btn btn-outline-secondary border-start-0"
                      type="button"
                      onClick={() => setSearchQuery('')}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TAB CONTENT */}
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body p-4">

            {/* PENDING REQUESTS */}
            {activeTab === 'pending' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0">
                    <i className="bi bi-clock-history me-2 text-primary"></i>
                    Pending Booking Requests
                    {searchQuery && (
                      <span className="badge bg-primary ms-2">
                        {filterBySearch(pendingAppointments).length} found
                      </span>
                    )}
                  </h5>
                </div>

                {filterBySearch(pendingAppointments).length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox fs-1 text-muted mb-3 d-block"></i>
                    <p className="text-muted">
                      {searchQuery ? 'No pending booking requests match your search.' : 'No pending booking requests.'}
                    </p>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {filterBySearch(pendingAppointments)
                      .sort((a, b) => {
                        // Sort by date and time (newest first)
                        const dateA = a.date || '';
                        const dateB = b.date || '';

                        if (!dateA && !dateB) return 0;
                        if (!dateA) return 1;
                        if (!dateB) return -1;

                        const timeA = a.start_time || '00:00:00';
                        const timeB = b.start_time || '00:00:00';

                        const dateTimeA = new Date(`${dateA} ${timeA}`);
                        const dateTimeB = new Date(`${dateB} ${timeB}`);

                        // Sort descending (newest first)
                        return dateTimeB - dateTimeA;
                      })
                      .map(a => (
                        <div key={a.appointment_id} className="list-group-item px-0 py-3 border-0 border-bottom bg-light bg-opacity-50 rounded-3 mb-2">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="bg-primary bg-opacity-10 text-white rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                  <i className="bi bi-person-fill"></i>
                                </div>
                                <div>
                                  <DoctorPatientInfo appointment={a} />
                                </div>
                              </div>
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                <span className="badge bg-white bg-opacity-10 text-primary border border-primary rounded-pill px-3">
                                  <i className="bi bi-calendar-event me-1"></i> {a.date}
                                </span>
                                <span className="badge bg-info bg-opacity-10 text-info border border-info rounded-pill px-3">
                                  <i className="bi bi-clock me-1"></i> {a.start_time} - {a.end_time}
                                </span>
                                <CallTypeBadge callType={a.call_type} />
                              </div>
                              {(a.medical_reports?.length
                                ? a.medical_reports
                                : (a.medical_report ? [a.medical_report] : [])
                              ).map((reportUrl, idx) => (
                                <div className="mt-2" key={`${reportUrl}-${idx}`}>
                                  <a
                                    href={mediaUrl(reportUrl)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-primary rounded-pill text-decoration-none px-3 py-1 fw-bold d-inline-flex align-items-center shadow-sm"
                                    style={{ fontSize: '0.82rem' }}
                                  >
                                    <i className="bi bi-file-earmark-medical-fill me-1 text-primary"></i>
                                    {a.medical_reports?.length > 1
                                      ? `View Report ${idx + 1}`
                                      : 'View Patient Attached Report'}
                                    <i className="bi bi-box-arrow-up-right ms-2" style={{ fontSize: '0.75rem' }}></i>
                                  </a>
                                </div>
                              ))}
                            </div>
                            <div className="d-flex flex-column gap-2 ms-3">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-info rounded-pill px-3"
                                onClick={() => setRecordsAppointment(a)}
                              >
                                <i className="bi bi-folder2-open me-1"></i>Records
                              </button>
                              <button
                                className="btn btn-sm btn-success rounded-pill px-3"
                                onClick={() => updateBookingStatus(a.appointment_id, 'accept')}
                              >
                                <i className="bi bi-check-circle me-1"></i>Accept
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger rounded-pill px-3"
                                onClick={() => {
                                  setAppointmentToReject(a.appointment_id);
                                  setRejectReason('');
                                  setShowRejectModal(true);
                                }}
                              >
                                <i className="bi bi-x-circle me-1"></i>Reject
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* UPCOMING APPOINTMENTS */}
            {activeTab === 'upcoming' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0">
                    <i className="bi bi-calendar-check me-2 text-success"></i>
                    Upcoming Appointments
                    {searchQuery && (
                      <span className="badge bg-success ms-2">
                        {filterBySearch(upcomingAppointments).length} found
                      </span>
                    )}
                  </h5>
                </div>

                {filterBySearch(upcomingAppointments).length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-calendar-x fs-1 text-muted mb-3 d-block"></i>
                    <p className="text-muted">
                      {searchQuery ? 'No upcoming appointments match your search.' : 'No upcoming appointments scheduled.'}
                    </p>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {filterBySearch(upcomingAppointments)
                      .sort((a, b) => {
                        // Sort by date and time (newest first)
                        const dateA = a.date || '';
                        const dateB = b.date || '';

                        if (!dateA && !dateB) return 0;
                        if (!dateA) return 1;
                        if (!dateB) return -1;

                        const timeA = a.start_time || '00:00:00';
                        const timeB = b.start_time || '00:00:00';

                        const dateTimeA = new Date(`${dateA} ${timeA}`);
                        const dateTimeB = new Date(`${dateB} ${timeB}`);

                        // Sort descending (newest first)
                        return dateTimeB - dateTimeA;
                      })
                      .map(a => (
                        <div key={a.appointment_id} className="list-group-item px-0 py-3 border-0 border-bottom">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="flex-grow-1">
                              <DoctorPatientInfo appointment={a} titleClass="fw-bold mb-1" subtitleClass="text-muted small mb-2" />
                              <div className="d-flex flex-wrap gap-2">
                                <span className="badge bg-success bg-opacity-10 text-success border border-success rounded-pill px-3">
                                  <i className="bi bi-calendar-check me-1"></i> {a.date}
                                </span>
                                <span className="badge bg-white bg-opacity-10 text-primary border border-primary rounded-pill px-3">
                                  <i className="bi bi-clock me-1"></i> {a.start_time} - {a.end_time}
                                </span>
                                <CallTypeBadge callType={a.call_type} />
                              </div>
                              {(a.medical_reports?.length
                                ? a.medical_reports
                                : (a.medical_report ? [a.medical_report] : [])
                              ).map((reportUrl, idx) => (
                                <div className="mt-2" key={`${reportUrl}-${idx}`}>
                                  <a
                                    href={mediaUrl(reportUrl)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-primary rounded-pill text-decoration-none px-3 py-1 fw-bold d-inline-flex align-items-center shadow-sm"
                                    style={{ fontSize: '0.82rem' }}
                                  >
                                    <i className="bi bi-file-earmark-medical-fill me-1 text-primary"></i>
                                    {a.medical_reports?.length > 1
                                      ? `View Report ${idx + 1}`
                                      : 'View Patient Attached Report'}
                                    <i className="bi bi-box-arrow-up-right ms-2" style={{ fontSize: '0.75rem' }}></i>
                                  </a>
                                </div>
                              ))}
                            </div>
                            <div className="d-flex gap-2 align-items-center flex-wrap">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-info rounded-pill px-3"
                                onClick={() => setRecordsAppointment(a)}
                              >
                                <i className="bi bi-folder2-open me-1"></i>Records
                              </button>
                              <DoctorVideoCallButton appointment={a} navigate={navigate} />
                              <button
                                className="btn btn-sm btn-outline-primary rounded-pill px-3"
                                onClick={() => {
                                  setSelectedAppointment(a);
                                  setShowDetailModal(true);
                                }}
                              >
                                <i className="bi bi-eye me-1"></i>View
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* SESSION EXPIRED APPOINTMENTS */}
            {activeTab === 'session-expired' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0">
                    <i className="bi bi-x-circle-fill me-2 text-danger"></i>
                    Session Expired Appointments
                    {searchQuery && (
                      <span className="badge bg-danger ms-2">
                        {filterBySearch(sessionExpiredAppointments).length} found
                      </span>
                    )}
                  </h5>
                </div>

                {filterBySearch(sessionExpiredAppointments).length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-calendar-check fs-1 text-muted mb-3 d-block"></i>
                    <p className="text-muted">
                      {searchQuery ? 'No expired sessions match your search.' : 'No expired sessions found.'}
                    </p>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {filterBySearch(sessionExpiredAppointments)
                      .sort((a, b) => {
                        const dateA = new Date(`${a.date} ${a.end_time}`);
                        const dateB = new Date(`${b.date} ${b.end_time}`);
                        return dateB - dateA;
                      })
                      .map(a => (
                        <div key={a.appointment_id} className="list-group-item px-0 py-3 border-0 border-bottom opacity-75">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="flex-grow-1">
                              <DoctorPatientInfo appointment={a} titleClass="fw-bold mb-1" subtitleClass="text-muted small mb-2" />
                              <div className="d-flex flex-wrap gap-2">
                                <span className="badge bg-danger bg-opacity-10 text-danger border border-danger rounded-pill px-3">
                                  <i className="bi bi-calendar-event me-1"></i> {a.date}
                                </span>
                                <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary rounded-pill px-3">
                                  <i className="bi bi-clock me-1"></i> {a.start_time} - {a.end_time}
                                </span>
                                <CallTypeBadge callType={a.call_type} />
                              </div>
                            </div>
                            <span className="badge bg-danger rounded-pill px-3">
                              <i className="bi bi-x-circle-fill me-1"></i>Session Expired
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* COMPLETED SESSION APPOINTMENTS */}
            {activeTab === 'completed-session' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0">
                    <i className="bi bi-check-circle-fill me-2 text-success"></i>
                    Completed Session Appointments
                    {searchQuery && (
                      <span className="badge bg-success ms-2">
                        {filterBySearch(completedSessionAppointments).length} found
                      </span>
                    )}
                  </h5>
                </div>

                {filterBySearch(completedSessionAppointments).length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-calendar-check fs-1 text-muted mb-3 d-block"></i>
                    <p className="text-muted">
                      {searchQuery ? 'No completed sessions match your search.' : 'No completed sessions found.'}
                    </p>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {filterBySearch(completedSessionAppointments)
                      .sort((a, b) => {
                        const dateA = new Date(`${a.date} ${a.end_time}`);
                        const dateB = new Date(`${b.date} ${b.end_time}`);
                        return dateB - dateA;
                      })
                      .map(a => (
                        <div key={a.appointment_id} className="list-group-item px-0 py-3 border-0 border-bottom">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="flex-grow-1">
                              <DoctorPatientInfo appointment={a} titleClass="fw-bold mb-1" subtitleClass="text-muted small mb-2" />
                              <div className="d-flex flex-wrap gap-2">
                                <span className="badge bg-success bg-opacity-10 text-success border border-success rounded-pill px-3">
                                  <i className="bi bi-calendar-check me-1"></i> {a.date}
                                </span>
                                <span className="badge bg-white bg-opacity-10 text-primary border border-primary rounded-pill px-3">
                                  <i className="bi bi-clock me-1"></i> {a.start_time} - {a.end_time}
                                </span>
                                <CallTypeBadge callType={a.call_type} />
                              </div>
                            </div>
                            <div className="d-flex flex-column align-items-end gap-2">
                              <span className="badge bg-success rounded-pill px-3">
                                <i className="bi bi-check-circle-fill me-1"></i>Completed
                              </span>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-info rounded-pill px-3"
                                onClick={() => setRecordsAppointment(a)}
                              >
                                <i className="bi bi-folder2-open me-1"></i>Records
                              </button>
                              <button
                                type="button"
                                className={`btn btn-sm rounded-pill px-3 ${
                                  a.has_prescription ? 'btn-outline-success' : 'btn-primary'
                                }`}
                                onClick={() => setPrescriptionAppointment(a)}
                              >
                                <i className="bi bi-file-medical me-1" />
                                {a.has_prescription ? 'View / update Rx' : 'Send prescription'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* MISSED APPOINTMENTS — doctor never Accept/Reject before slot end */}
            {activeTab === 'missed' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0">
                    <i className="bi bi-exclamation-triangle me-2 text-warning"></i>
                    Missed Bookings
                    {searchQuery && (
                      <span className="badge bg-warning text-dark ms-2">
                        {filterBySearch(missedAppointments).length} found
                      </span>
                    )}
                  </h5>
                </div>
                <p className="text-muted small mb-3">
                  Pending requests where Accept/Reject was not done before the slot ended. Patient was refunded.
                </p>

                {filterBySearch(missedAppointments).length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox fs-1 text-muted mb-3 d-block"></i>
                    <p className="text-muted">
                      {searchQuery ? 'No missed bookings match your search.' : 'No missed bookings.'}
                    </p>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {filterBySearch(missedAppointments)
                      .sort((a, b) => {
                        const dateA = new Date(`${a.date} ${a.start_time}`);
                        const dateB = new Date(`${b.date} ${b.start_time}`);
                        return dateB - dateA;
                      })
                      .map(a => (
                        <div key={a.appointment_id} className="list-group-item px-0 py-3 border-0 border-bottom opacity-75">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="flex-grow-1">
                              <DoctorPatientInfo appointment={a} titleClass="fw-bold mb-1" subtitleClass="text-muted small mb-2" />
                              <div className="d-flex flex-wrap gap-2">
                                <span className="badge bg-warning bg-opacity-10 text-warning border border-warning rounded-pill px-3">
                                  <i className="bi bi-calendar-event me-1"></i> {a.date}
                                </span>
                                <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary rounded-pill px-3">
                                  <i className="bi bi-clock me-1"></i> {a.start_time} - {a.end_time}
                                </span>
                                <CallTypeBadge callType={a.call_type} />
                              </div>
                              {a.rejection_reason && (
                                <p className="text-muted small mt-2 mb-0">
                                  <i className="bi bi-info-circle me-1"></i>
                                  {a.rejection_reason}
                                </p>
                              )}
                            </div>
                            <span className="badge bg-warning text-dark rounded-pill px-3">
                              Missed
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* REJECTED APPOINTMENTS */}
            {activeTab === 'rejected' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0">
                    <i className="bi bi-x-circle me-2 text-danger"></i>
                    Rejected Appointments
                    {searchQuery && (
                      <span className="badge bg-danger ms-2">
                        {filterBySearch(rejectedAppointments).length} found
                      </span>
                    )}
                  </h5>
                </div>

                {filterBySearch(rejectedAppointments).length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox fs-1 text-muted mb-3 d-block"></i>
                    <p className="text-muted">
                      {searchQuery ? 'No rejected appointments match your search.' : 'No rejected appointments.'}
                    </p>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {filterBySearch(rejectedAppointments)
                      .sort((a, b) => {
                        const dateA = new Date(`${a.date} ${a.start_time}`);
                        const dateB = new Date(`${b.date} ${b.start_time}`);
                        return dateB - dateA;
                      })
                      .map(a => (
                        <div key={a.appointment_id} className="list-group-item px-0 py-3 border-0 border-bottom opacity-75">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="flex-grow-1">
                              <DoctorPatientInfo appointment={a} titleClass="fw-bold mb-1" subtitleClass="text-muted small mb-2" />
                              <div className="d-flex flex-wrap gap-2">
                                <span className="badge bg-danger bg-opacity-10 text-danger border border-danger rounded-pill px-3">
                                  <i className="bi bi-calendar-event me-1"></i> {a.date}
                                </span>
                                <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary rounded-pill px-3">
                                  <i className="bi bi-clock me-1"></i> {a.start_time} - {a.end_time}
                                </span>
                                <CallTypeBadge callType={a.call_type} />
                              </div>
                              {a.rejection_reason && (
                                <p className="text-muted small mt-2 mb-0">
                                  <i className="bi bi-info-circle me-1"></i>
                                  Reason: {a.rejection_reason}
                                </p>
                              )}
                            </div>
                            <span className="badge bg-danger rounded-pill px-3">
                              Rejected
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* ALL SLOTS */}
            {activeTab === 'slots' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0">
                    <i className="bi bi-calendar3 me-2 text-info"></i>
                    All Availability Slots
                    {searchQuery && (
                      <span className="badge bg-info ms-2">
                        {filterSlotsBySearch(slots).length} found
                      </span>
                    )}
                  </h5>
                  <button className="btn btn-sm btn-primary rounded-pill" onClick={() => setShowSlotModal(true)}>
                    <i className="bi bi-plus-lg me-1"></i>New Slot
                  </button>
                </div>

                <div className="list-group list-group-flush overflow-auto" style={{ maxHeight: '600px' }}>
                  {filterSlotsBySearch(slots).length === 0 ? (
                    <p className="text-muted text-center py-4">
                      {searchQuery ? 'No slots match your search.' : 'No slots created yet.'}
                    </p>
                  ) : (
                    filterSlotsBySearch([...slots]).sort((a, b) => {
                      const dateA = a.date || a.date_from || '';
                      const dateB = b.date || b.date_from || '';

                      if (!dateA || dateA === 'N/A') return 1;
                      if (!dateB || dateB === 'N/A') return -1;

                      const dateAObj = new Date(dateA);
                      const dateBObj = new Date(dateB);

                      if (dateAObj.getTime() === dateBObj.getTime()) {
                        const timeA = a.start_time || '';
                        const timeB = b.start_time || '';
                        return timeA.localeCompare(timeB);
                      }

                      return dateBObj.getTime() - dateAObj.getTime();
                    }).map(s => {
                      const slotDate = s.date || s.date_from || 'N/A';
                      const slotDay = s.day_of_week || '';
                      const isPast = slotDate && new Date(slotDate) < new Date().setHours(0, 0, 0, 0);

                      return (
                        <div key={s.id} className={`list-group-item px-0 py-3 border-0 border-bottom ${isPast ? 'opacity-50' : ''}`}>
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <p className="fw-bold mb-0 small">{slotDate}</p>
                                {slotDay && <span className="badge bg-light text-dark small">{slotDay}</span>}
                                <CallTypeBadge callType={s.call_type} />
                              </div>
                              <p className="text-muted mb-0 small">
                                <i className="bi bi-clock me-1"></i>
                                {s.start_time} - {s.end_time}
                                {s.duration && <span className="ms-2 text-info">({s.duration} min)</span>}
                              </p>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              {s.is_booked ? (
                                <span className="badge bg-success rounded-pill px-3">
                                  <i className="bi bi-check-circle me-1"></i>Booked
                                </span>
                              ) : (
                                <>
                                  <div className="form-check form-switch me-2">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={s.is_active}
                                      onChange={() => toggleSlotStatus(s.id)}
                                      title={s.is_active ? "Active - Click to deactivate" : "Inactive - Click to activate"}
                                    />
                                  </div>
                                  <button
                                    className="btn btn-sm btn-outline-danger border-0"
                                    onClick={() => handleDeleteSlotClick(s.id)}
                                    title="Delete slot"
                                    disabled={isPast}
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* BOOKED SLOTS */}
            {activeTab === 'booked' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0">
                    <i className="bi bi-calendar-check me-2 text-success"></i>
                    Booked Slots
                    {searchQuery && (
                      <span className="badge bg-success ms-2">
                        {filterSlotsBySearch(slots.filter(s => s.is_booked)).length} found
                      </span>
                    )}
                  </h5>
                </div>

                <div className="list-group list-group-flush overflow-auto" style={{ maxHeight: '600px' }}>
                  {filterSlotsBySearch(slots.filter(s => s.is_booked)).length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-calendar-x fs-1 text-muted mb-3 d-block"></i>
                      <p className="text-muted">
                        {searchQuery ? 'No booked slots match your search.' : 'No booked slots found.'}
                      </p>
                    </div>
                  ) : (
                    filterSlotsBySearch([...slots])
                      .filter(s => s.is_booked)
                      .sort((a, b) => {
                        const dateA = a.date || a.date_from || '';
                        const dateB = b.date || b.date_from || '';

                        if (!dateA || dateA === 'N/A') return 1;
                        if (!dateB || dateB === 'N/A') return -1;

                        const dateAObj = new Date(dateA);
                        const dateBObj = new Date(dateB);

                        if (dateAObj.getTime() === dateBObj.getTime()) {
                          const timeA = a.start_time || '';
                          const timeB = b.start_time || '';
                          return timeA.localeCompare(timeB);
                        }

                        return dateBObj.getTime() - dateAObj.getTime();
                      })
                      .map(s => {
                        const slotDate = s.date || s.date_from || 'N/A';
                        const slotDay = s.day_of_week || '';
                        const isPast = slotDate && new Date(slotDate) < new Date().setHours(0, 0, 0, 0);

                        // Find matching appointment for this slot
                        const matchingAppointment = findAppointmentForSlot(s);

                        // Determine button state for booked slot
                        let buttonState = null;
                        if (matchingAppointment && matchingAppointment.status === 'COMPLETED') {
                          buttonState = 'completed';
                        } else if (matchingAppointment && matchingAppointment.status === 'ACCEPTED') {
                          // Use same logic as DoctorVideoCallButton
                          const slotDateObj = slotDate !== 'N/A' ? new Date(slotDate) : null;
                          if (slotDateObj && s.start_time && s.end_time) {
                            const [startHour, startMin] = s.start_time.split(':').map(Number);
                            const [endHour, endMin] = s.end_time.split(':').map(Number);

                            const startDateTime = new Date(slotDateObj);
                            startDateTime.setHours(startHour, startMin, 0, 0);

                            const endDateTime = new Date(slotDateObj);
                            endDateTime.setHours(endHour, endMin, 0, 0);

                            const now = new Date();
                            const earlyBuffer = 5 * 60 * 1000; // 5 min before
                            const lateBuffer = 0; // no grace after slot end

                            const adjustedStart = new Date(startDateTime.getTime() - earlyBuffer);
                            const adjustedEnd = new Date(endDateTime.getTime() + lateBuffer);

                            if (now > adjustedEnd) {
                              buttonState = 'uncompleted';
                            } else if (now >= adjustedStart && now <= adjustedEnd) {
                              buttonState = 'join';
                            } else {
                              buttonState = 'future';
                            }
                          } else {
                            buttonState = 'join'; // Default to join if timing unclear
                          }
                        }

                        return (
                          <div key={s.id} className={`list-group-item px-0 py-3 border-0 border-bottom ${isPast ? 'opacity-50' : ''}`}>
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <p className="fw-bold mb-0 small">{slotDate}</p>
                                  {slotDay && <span className="badge bg-light text-dark small">{slotDay}</span>}
                                  <CallTypeBadge callType={s.call_type} />
                                </div>
                                <p className="text-muted mb-0 small">
                                  <i className="bi bi-clock me-1"></i>
                                  {s.start_time} - {s.end_time}
                                  {s.duration && <span className="ms-2 text-info">({s.duration} min)</span>}
                                </p>
                                {matchingAppointment && (
                                  <p className="text-muted mb-0 small mt-1">
                                    <i className="bi bi-person me-1"></i>
                                    Patient: {matchingAppointment.patient_name || 'N/A'}
                                    {matchingAppointment.booked_by_display
                                      ? ` (${matchingAppointment.booked_by_display})`
                                      : ''}
                                  </p>
                                )}
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                {buttonState === 'completed' ? (
                                  <span className="badge bg-success rounded-pill px-3">
                                    <i className="bi bi-check-circle-fill me-1"></i>Completed
                                  </span>
                                ) : buttonState === 'join' ? (
                                  <button
                                    onClick={() => navigate(`/doctor/waiting-room/${matchingAppointment.appointment_id}`)}
                                    className="btn btn-sm btn-success rounded-pill px-3"
                                    style={{ animation: 'pulse 2s infinite' }}
                                  >
                                    <i className={`bi ${matchingAppointment.call_type === 'AUDIO' ? 'bi-telephone-fill' : 'bi-camera-video-fill'} me-1`}></i>
                                    {matchingAppointment.call_type === 'AUDIO' ? 'Join Audio' : 'Join Call'}
                                  </button>
                                ) : buttonState === 'uncompleted' ? (
                                  <span className="badge bg-danger rounded-pill px-3">
                                    <i className="bi bi-x-circle-fill me-1"></i>Session Expired
                                  </span>
                                ) : buttonState === 'future' ? (
                                  <span className="badge bg-warning text-dark rounded-pill px-3">
                                    <i className="bi bi-clock me-1"></i>Scheduled
                                  </span>
                                ) : (
                                  <span className="badge bg-success rounded-pill px-3">
                                    <i className="bi bi-person-check me-1"></i>Booked
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* CREATE SLOT MODAL */}
      {showSlotModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold">Create Availability Slots</h5>
                <button type="button" className="btn-close" onClick={() => setShowSlotModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <form onSubmit={handleCreateSlot}>
                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <label className="form-label small fw-bold">Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        required
                        min={new Date().toISOString().split('T')[0]}
                        value={slotForm.start_date}
                        onChange={(e) => setSlotForm({ ...slotForm, start_date: e.target.value })}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-bold">End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        required
                        min={slotForm.start_date || new Date().toISOString().split('T')[0]}
                        value={slotForm.end_date}
                        onChange={(e) => setSlotForm({ ...slotForm, end_date: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <label className="form-label small fw-bold">Start Time</label>
                      <input
                        type="time"
                        className="form-control"
                        required
                        value={slotForm.start_time}
                        onChange={(e) => setSlotForm({ ...slotForm, start_time: e.target.value })}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-bold">End Time</label>
                      <input
                        type="time"
                        className="form-control"
                        required
                        value={slotForm.end_time}
                        onChange={(e) => setSlotForm({ ...slotForm, end_time: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Call Type</label>
                    <div className="d-flex gap-2">
                      {['VIDEO', 'AUDIO'].map((type) => (
                        <button
                          key={type}
                          type="button"
                          className={`btn flex-fill rounded-pill ${slotForm.call_type === type ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setSlotForm({ ...slotForm, call_type: type })}
                        >
                          <i className={`bi ${type === 'VIDEO' ? 'bi-camera-video' : 'bi-telephone'} me-1`}></i>
                          {type === 'VIDEO' ? 'Video Call' : 'Audio Call'}
                        </button>
                      ))}
                    </div>
                    <small className="text-muted">Same time cannot be used for both audio and video.</small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Slot Duration (Minutes)</label>
                    <FilterSelect
                      value={String(slotForm.duration)}
                      onChange={(duration) => setSlotForm({ ...slotForm, duration })}
                      options={SLOT_DURATION_OPTIONS}
                      placeholder="Slot Duration"
                      aria-label="Slot Duration"
                      variant="bordered"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="form-label small fw-bold mb-2">Select Days</label>
                    <div className="d-flex flex-wrap gap-2">
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                        <button
                          key={day}
                          type="button"
                          className={`btn rounded-pill px-3 ${slotForm.days.includes(day)
                            ? 'btn-primary'
                            : 'btn-outline-primary'
                            }`}
                          onClick={() => toggleDay(day)}
                        >
                          {day.substring(0, 3)}
                        </button>
                      ))}
                    </div>
                    {slotForm.days.length > 0 && (
                      <p className="small text-muted mt-2 mb-0">
                        Selected: {slotForm.days.join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="d-grid">
                    <button type="submit" className="btn btn-primary btn-lg rounded-3 fw-bold">
                      <i className="bi bi-calendar-plus me-2"></i>Generate Slots
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* APPOINTMENT DETAIL MODAL */}
      {showDetailModal && selectedAppointment && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-primary">Appointment Details</h5>
                <button type="button" className="btn-close" onClick={() => setShowDetailModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="mb-4 text-center">
                  <div className="bg-primary bg-opacity-10 text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                    <i className="bi bi-person-fill fs-2"></i>
                  </div>
                  <DoctorPatientInfo
                    appointment={selectedAppointment}
                    titleClass="fw-bold mb-0"
                    subtitleClass="text-muted small"
                  />
                </div>

                <div className="row g-3">
                  <div className="col-6">
                    <label className="text-muted small fw-bold text-uppercase d-block">Date</label>
                    <p className="fw-medium mb-0"><i className="bi bi-calendar-event me-2 text-primary"></i>{selectedAppointment.date}</p>
                  </div>
                  <div className="col-6">
                    <label className="text-muted small fw-bold text-uppercase d-block">Time</label>
                    <p className="fw-medium mb-0"><i className="bi bi-clock me-2 text-primary"></i>{selectedAppointment.start_time}</p>
                  </div>
                  <div className="col-6">
                    <label className="text-muted small fw-bold text-uppercase d-block">Call Type</label>
                    <CallTypeBadge callType={selectedAppointment.call_type} />
                  </div>
                  <div className="col-12 border-top pt-3">
                    <label className="text-muted small fw-bold text-uppercase d-block">Status</label>
                    <span className={`badge rounded-pill ${selectedAppointment.status === 'COMPLETED' ? 'bg-success' :
                      selectedAppointment.status === 'ACCEPTED' ? 'bg-success' : 'bg-warning text-dark'
                      }`}>
                      {selectedAppointment.status === 'COMPLETED' ? 'Completed' : selectedAppointment.status}
                    </span>
                  </div>
                </div>

                <div className="mt-4 d-grid">
                  <button className="btn btn-primary rounded-3 fw-bold" onClick={() => setShowDetailModal(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE SLOT CONFIRMATION MODAL */}
      {showDeleteConfirm && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>Confirm Delete
                </h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowDeleteConfirm(false);
                  setSlotToDelete(null);
                }}></button>
              </div>
              <div className="modal-body p-4">
                <p className="mb-0">
                  Are you sure you want to delete this slot?
                </p>
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-3"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setSlotToDelete(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger rounded-3 fw-bold"
                  onClick={handleDeleteSlotConfirm}
                >
                  <i className="bi bi-trash me-2"></i>Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* REJECT APPOINTMENT MODAL */}
      {showRejectModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-danger">
                  <i className="bi bi-x-circle me-2"></i>Reject Appointment
                </h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowRejectModal(false);
                  setAppointmentToReject(null);
                  setRejectReason('');
                }}></button>
              </div>
              <div className="modal-body p-4">
                <label className="form-label fw-bold">Rejection Reason (Optional)</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Enter rejection reason..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                ></textarea>
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-3"
                  onClick={() => {
                    setShowRejectModal(false);
                    setAppointmentToReject(null);
                    setRejectReason('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger rounded-3 fw-bold"
                  onClick={() => {
                    if (appointmentToReject) {
                      updateBookingStatus(appointmentToReject, 'reject', rejectReason || "");
                      setShowRejectModal(false);
                      setAppointmentToReject(null);
                      setRejectReason('');
                    }
                  }}
                >
                  <i className="bi bi-x-circle me-2"></i>Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {prescriptionAppointment && (
        <SendPrescriptionModal
          appointment={prescriptionAppointment}
          onClose={() => setPrescriptionAppointment(null)}
          onSaved={() => doctorId && loadDashboardData(doctorId, false)}
        />
      )}

      {recordsAppointment && (
        <PatientRecordsModal
          appointment={recordsAppointment}
          onClose={() => setRecordsAppointment(null)}
        />
      )}
    </div>
  );
}

/**
 * DoctorVideoCallButton Component
 * 
 * Smart button based on appointment timing:
 * - Past (Completed): Shows "Completed" badge
 * - Current (Within slot time): Shows "Join Call" button  
 * - Future: Shows "Scheduled" with time
 */
function DoctorVideoCallButton({ appointment, navigate }) {
  const [status, setStatus] = useState('loading'); // loading, completed, current, future
  const [timeInfo, setTimeInfo] = useState('');

  useEffect(() => {
    const calculateStatus = () => {
      if (!appointment.date || !appointment.start_time || !appointment.end_time) {
        setStatus('current'); // Default to allowing join
        return;
      }

      // Parse date and times
      const slotDate = new Date(appointment.date);
      const [startHour, startMin] = appointment.start_time.split(':').map(Number);
      const [endHour, endMin] = appointment.end_time.split(':').map(Number);

      const startDateTime = new Date(slotDate);
      startDateTime.setHours(startHour, startMin, 0, 0);

      const endDateTime = new Date(slotDate);
      endDateTime.setHours(endHour, endMin, 0, 0);

      const now = new Date();

      // 5 minutes buffer before; no grace after slot end
      const earlyBuffer = 5 * 60 * 1000; // 5 min before
      const lateBuffer = 0;

      const adjustedStart = new Date(startDateTime.getTime() - earlyBuffer);
      const adjustedEnd = new Date(endDateTime.getTime() + lateBuffer);

      if (now > adjustedEnd) {
        // Past - session completed
        setStatus('completed');
      } else if (now >= adjustedStart && now <= adjustedEnd) {
        // Current - can join now
        setStatus('current');
      } else {
        // Future - show scheduled time
        setStatus('future');

        // Calculate time until start
        const diffMs = startDateTime - now;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        if (diffHours > 24) {
          const days = Math.floor(diffHours / 24);
          setTimeInfo(`in ${days} day${days > 1 ? 's' : ''}`);
        } else if (diffHours > 0) {
          setTimeInfo(`in ${diffHours}h ${diffMins}m`);
        } else if (diffMins > 0) {
          setTimeInfo(`in ${diffMins}m`);
        } else {
          setTimeInfo('starting soon');
        }
      }
    };

    calculateStatus();
    const interval = setInterval(calculateStatus, 1000);
    return () => clearInterval(interval);
  }, [appointment]);

  const handleClick = () => {
    navigate(`/doctor/waiting-room/${appointment.appointment_id}`);
  };

  // Loading state
  if (status === 'loading') {
    return (
      <span className="badge bg-secondary rounded-pill px-3 py-2">
        <i className="bi bi-hourglass-split me-1"></i>Loading...
      </span>
    );
  }

  // Session Expired - past session (missed/not attended)
  if (status === 'completed') {
    return (
      <span className="badge bg-danger rounded-pill px-3 py-2">
        <i className="bi bi-x-circle-fill me-1"></i>Session Expired
      </span>
    );
  }

  // Future - scheduled
  if (status === 'future') {
    return (
      <span className="badge bg-warning text-dark rounded-pill px-3 py-2">
        <i className="bi bi-clock me-1"></i>Scheduled {timeInfo}
      </span>
    );
  }

  // Current - can join
  return (
    <button
      onClick={handleClick}
      className="btn btn-sm btn-success rounded-pill px-3"
      style={{ animation: 'pulse 2s infinite' }}
    >
      <i className={`bi ${appointment.call_type === 'AUDIO' ? 'bi-telephone-fill' : 'bi-camera-video-fill'} me-1`}></i>
      {appointment.call_type === 'AUDIO' ? 'Join Audio' : 'Join Call'}
    </button>
  );
}

export default DoctorAppointments;
